import { X } from 'lucide-react';
import { computeUnitDays, computeUnitTotal } from '@jobos/shared';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { dateToLocalISO } from '@/lib/format';
import { MoneyInput } from './MoneyInput';
import {
  clampUnitDiscount,
  type EquipmentUnitUI,
  type ResolvedUnitType,
} from './equipment-helpers';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export interface UnitTypeGroupProps {
  type: ResolvedUnitType;
  units: EquipmentUnitUI[];
  onUpdate: (unitId: string, patch: Partial<EquipmentUnitUI>) => void;
  onRemove: (unitId: string) => void;
  readOnly?: boolean;
}

/** Net of this unit's discount — matches what the bill and invoice charge. */
function unitTotal(u: EquipmentUnitUI, t: ResolvedUnitType): number {
  const gross = computeUnitTotal({
    assetTag: u.assetTag,
    location: u.location,
    startDate: u.startDate,
    endDate: u.endDate,
    days: u.days ?? null,
    setupFee: t.setupFee,
    dailyRate: t.dailyRate,
    minDays: t.minDays,
  });
  return gross - clampUnitDiscount(u, t);
}

export function UnitTypeGroup({ type, units, onUpdate, onRemove, readOnly = false }: UnitTypeGroupProps) {
  const setup = type.setupFee;
  const daily = type.dailyRate;
  const subtotal = units.reduce((sum, u) => sum + unitTotal(u, type), 0);
  const minHint = type.minDays > 0 ? ` · min ${type.minDays} day` : '';
  // Custom equipment has no asset tag, so the group name is what identifies a
  // unit in labels and in the Asset ID cell.
  const unitName = (u: EquipmentUnitUI) => u.assetTag || type.label;

  return (
    // Indented left rail nests every type group under the "Equipment" section
    // header so the two no longer read as peers.
    <section className="pl-3 border-l-2 border-gray-100 pt-3 pb-4 mb-4 last:mb-0">
      {/* Sub-level header: lighter weight than the section header above it. */}
      <header className="flex items-baseline gap-2 pb-1.5">
        <span className="text-xs font-medium text-gray-700">{type.label}{units.length > 1 ? 's' : ''}</span>
        <span className="text-[11px] text-gray-400">
          setup {money(setup)} + {money(daily)}/day{minHint}
        </span>
        <span className="ml-auto text-xs tabular-nums text-gray-500 pr-11">
          {money(subtotal)}
        </span>
      </header>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[940px] table-fixed text-sm mt-1">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-gray-400">
            <th className="text-left py-1.5 pr-2 font-medium w-[70px]">Asset ID</th>
            <th className="text-left py-1.5 pr-2 font-medium w-[140px]">Location</th>
            <th className="text-left py-1.5 pr-2 font-medium">Rental period</th>
            <th className="text-right py-1.5 pr-2 font-medium w-[60px]">Days</th>
            <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Discount</th>
            <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Total</th>
            <th className="w-9"></th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const daysMode = u.days != null;
            const days = computeUnitDays({
              startDate: u.startDate,
              endDate: u.endDate,
              minDays: type.minDays,
              days: u.days,
            });
            const hasPeriod = daysMode || !!(u.startDate && u.endDate);
            const total = unitTotal(u, type);
            const enableDays = () => {
              const seeded =
                computeUnitDays({ startDate: u.startDate, endDate: u.endDate, minDays: type.minDays }) ||
                type.minDays ||
                1;
              onUpdate(u.id, { days: seeded, startDate: null, endDate: null });
            };
            return (
              <tr key={u.id} className="border-t border-gray-50 group">
                <td className="py-1.5 pr-2">
                  {u.assetTag ? (
                    <span className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-semibold">
                      {u.assetTag}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className="py-1.5 pr-2">
                  <Input
                    value={u.location ?? ''}
                    placeholder="Location"
                    disabled={readOnly}
                    onChange={(e) => onUpdate(u.id, { location: e.target.value || null })}
                    className="h-7 text-sm max-w-[220px]"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <div className="flex items-center gap-2 w-full min-w-[412px]">
                    {!readOnly && (
                      <div className="inline-flex shrink-0 rounded-md border border-gray-200 overflow-hidden">
                        {(['dates', 'days'] as const).map((m) => {
                          const active = m === (daysMode ? 'days' : 'dates');
                          return (
                            <button
                              key={m}
                              type="button"
                              aria-pressed={active}
                              onClick={() => {
                                if (active) return;
                                if (m === 'days') enableDays();
                                else onUpdate(u.id, { days: null });
                              }}
                              className={
                                'px-2 py-1 text-[11px] capitalize transition-colors ' +
                                (active
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-white text-gray-600 hover:bg-gray-50')
                              }
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {daysMode ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          aria-label="Days"
                          value={u.days ?? ''}
                          disabled={readOnly}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            onUpdate(u.id, {
                              days: Number.isNaN(parsed) || parsed < 0 ? 0 : parsed,
                            });
                          }}
                          className="h-7 text-sm w-20 tabular-nums"
                        />
                        <span className="text-xs text-gray-400 whitespace-nowrap">days est.</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <div className="min-w-[152px] max-w-[164px] flex-1">
                          <DatePicker
                            aria-label={`Start date for ${unitName(u)}`}
                            value={u.startDate ?? null}
                            disabled={readOnly}
                            placeholder="Start"
                            onChange={(d) => onUpdate(u.id, { startDate: d ? dateToLocalISO(d) : null })}
                            className="h-7 text-sm pr-16"
                          />
                        </div>
                        <span className="text-gray-300">–</span>
                        <div className="min-w-[152px] max-w-[164px] flex-1">
                          <DatePicker
                            aria-label={`End date for ${unitName(u)}`}
                            value={u.endDate ?? null}
                            disabled={readOnly}
                            placeholder="End"
                            onChange={(d) => onUpdate(u.id, { endDate: d ? dateToLocalISO(d) : null })}
                            className="h-7 text-sm pr-16"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                  {hasPeriod ? days : '—'}
                </td>
                <td className="py-1.5 pr-2 text-right">
                  <MoneyInput
                    mode="currency"
                    ariaLabel={`Discount for ${unitName(u)}`}
                    value={u.discount ?? 0}
                    disabled={readOnly}
                    onChange={(v) => onUpdate(u.id, { discount: v > 0 ? v : null })}
                    className="w-20 ml-auto"
                  />
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-gray-900">
                  {money(total)}
                </td>
                <td className="py-1.5 w-9 text-center">
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onRemove(u.id)}
                      aria-label={`Remove ${unitName(u)}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </section>
  );
}
